import Config from '../../config';
import { METHODS } from '../constants';
import { QueryError } from '../queries';
import { OnSuccessCallback, useCustomMutation } from './index';
import { ValidationResult, WithValidationData } from '../types';
import { ExcelUser, UserExpiration } from '../queries/users';
import { $externalUpdates } from '../../administration/shared/importHelpers';
import { userRolesDictionary, userRolesLabelToNameDictionary } from '../authorization/userRoles';


export type WithExternalUpdates<TData> = {
  [$externalUpdates]?: Partial<Record<keyof TData, boolean>>,
}

export enum RemoveTypes {
  Privilege = 'Privilege',
  Assignment = 'Assignment'
}

export type UserWithValidationData = (ExcelUser & WithValidationData<ExcelUser> & WithExternalUpdates<ExcelUser>)

export const useUploadUsers = (
  onSuccess?: (users: UserWithValidationData[]) => void
) =>
  useCustomMutation<Response, QueryError, ExcelUser[]>({
    handleData: users => ({
      url: Config.endpoints.VALIDATE_USERS,
      method: METHODS.POST,
      body: users,
    }),
    invalidateKey: [],
    errorMessage: 'Uploading users failed',
    successMessage: 'Import validated',
    onSuccess: async (response, usersRows: ExcelUser[]) => {
      const validationResult = await response.json() as {
        errors: ValidationResult<ExcelUser>,
        updatingRows: Record<number, { assignments: boolean, roleName: boolean, privileges: boolean }>
      };

      const usersWithValidationData = usersRows.map((user, index) => {
        const newUser: UserWithValidationData = {
          ...user,
          ...validationResult.errors[index] ? {
            validationData: validationResult.errors[index],
          } : {},
          ...validationResult.updatingRows[index] ? {
            [$externalUpdates]: validationResult.updatingRows[index],
          } : {},
        };
        return { ...newUser, roleName: userRolesDictionary[newUser.roleName] || newUser.roleName };
      });

      onSuccess?.(usersWithValidationData);
    },
  });

export const useImportUsers = (
  onSuccess?: OnSuccessCallback<Response, ExcelUser[]>
) =>
  useCustomMutation<Response, QueryError, ExcelUser[]>({
    handleData: users => ({
      url: Config.endpoints.IMPORT_USERS,
      method: METHODS.POST,
      body: users.map(user => ({ ...user, roleName: userRolesLabelToNameDictionary[user.roleName] || user.roleName })),
    }),
    invalidateKey: [],
    errorMessage: 'Importing users failed',
    successMessage: 'Users imported successfully',
    onSuccess,
  });

export const useUpdateExpirationUsers = (
  onSuccess?: OnSuccessCallback<Response, UserExpiration[]>
) =>
  useCustomMutation<Response, QueryError, UserExpiration[]>({
    handleData: users => ({
      url: Config.endpoints.UPDATE_USERS_EXPIRATION,
      method: METHODS.PUT,
      body: users,
    }),
    invalidateKey: [],
    errorMessage: 'Update users expiration date failed',
    successMessage: 'Update users expiration date successfully',
    onSuccess,
  });
